// ============================================
// Address Utilities - Normalization & Matching
// ============================================
// Used by property auto-link (App.jsx) and property migration (crm-migration.js)
// for fuzzy address matching and deduplication.

/**
 * Normalize an Australian street address for comparison.
 * Strips unit prefixes, normalizes street types, collapses whitespace.
 */
export function normalizeAddress(raw: any) {
    if (!raw) return '';
    return raw.toLowerCase()
        // Strip unit/apt/suite/lot prefixes
        .replace(/^(unit|apt|apartment|suite|lot|level)\s*\d+\s*[,/\\]?\s*/i, '')
        // Strip leading numbers with slash (e.g., "5/12 Smith St" -> "12 Smith St")
        .replace(/^\d+\s*[/\\]\s*/, '')
        // Normalize street type abbreviations
        .replace(/\bstreet\b/g, 'st')
        .replace(/\broad\b/g, 'rd')
        .replace(/\bavenue\b/g, 'ave')
        .replace(/\bdrive\b/g, 'dr')
        .replace(/\bplace\b/g, 'pl')
        .replace(/\bcrescent\b/g, 'cr')
        .replace(/\bcourt\b/g, 'ct')
        .replace(/\bparade\b/g, 'pde')
        .replace(/\bterrace\b/g, 'tce')
        .replace(/\blane\b/g, 'ln')
        .replace(/\bboulevard\b/g, 'blvd')
        .replace(/\bhighway\b/g, 'hwy')
        .replace(/\bcircuit\b/g, 'cct')
        .replace(/\bclose\b/g, 'cl')
        .replace(/\bway\b/g, 'wy')
        // Collapse whitespace
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Normalize a strata plan number for comparison.
 * Removes spaces, uppercase: "SP 12345" -> "SP12345"
 */
export function normalizeStrataPlan(sp: any) {
    if (!sp) return '';
    return sp.replace(/\s+/g, '').toUpperCase();
}

/**
 * Extract suburb from an Australian address string.
 * Handles formats like: "42 Smith St, Bondi" or "42 Smith St Bondi NSW 2026"
 */
export function extractSuburb(address: any) {
    if (!address) return '';
    // Try comma-separated: "42 Smith St, Bondi, NSW"
    const parts = address.split(',').map((p: any) => p.trim());
    if (parts.length >= 2) {
        // Second part is likely suburb (possibly with state/postcode)
        const suburbia = parts[1].replace(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/gi, '').replace(/\d{4}/, '').trim();
        if (suburbia) return suburbia;
    }
    // Try extracting suburb before state abbreviation
    const stateMatch = address.match(/(.+?)\s+(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\s*\d{4}/i);
    if (stateMatch) {
        // Last word before state is likely suburb
        const beforeState = stateMatch[1].trim().split(/\s+/);
        if (beforeState.length >= 2) return beforeState[beforeState.length - 1];
    }
    return '';
}

/**
 * Extract 4-digit Australian postcode from an address string.
 *
 * Scans from the END of the string so that street numbers ("12345 Main St,
 * Sydney 2000") don't hijack the result. Only accepts values in the valid
 * AU postcode range 800-9999 (NT starts at 0800; Number('0800') === 800).
 *
 * Returns '' on no match for backward compatibility with existing callers.
 */
export function extractPostcode(address: any) {
    if (!address) return '';
    const matches = address.match(/\b(\d{4})\b/g);
    if (!matches || matches.length === 0) return '';
    for (let i = matches.length - 1; i >= 0; i--) {
        const n = Number(matches[i]);
        if (n >= 800 && n <= 9999) return matches[i];
    }
    return '';
}

/**
 * Extract state abbreviation from an address string.
 */
export function extractState(address: any) {
    if (!address) return 'NSW';
    const match = address.match(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/i);
    return match ? match[1].toUpperCase() : 'NSW';
}

/**
 * Compute Jaccard similarity between two address strings.
 * Returns 0-1 (1 = identical word sets).
 */
export function addressSimilarity(a: any, b: any) {
    if (!a || !b) return 0;
    const wordsA = new Set(normalizeAddress(a).split(/\s+/));
    const wordsB = new Set(normalizeAddress(b).split(/\s+/));
    if (wordsA.size === 0 || wordsB.size === 0) return 0;
    const intersection = [...wordsA].filter(w => wordsB.has(w)).length;
    const union = new Set([...wordsA, ...wordsB]).size;
    return union > 0 ? intersection / union : 0;
}

// ── Human-readable display formatting ───────────────────────────────────────
// normalizeAddress() above is a COMPARISON key (lowercased + abbreviated for
// dedup/matching). It is NOT for display. formatAddressForDisplay() is the
// opposite: it produces the clean, client-facing form for the quote + UI,
// fixing the common problem where site addresses arrive ALL CAPS from RFQ /
// strata documents (e.g. "6/37-41 VICTORIA STREET, EPPING NSW 2121") and get
// passed straight through by AI extraction.
//
// Design: this normalises CASE only. It does NOT re-parse structure, insert
// commas, or expand street-type abbreviations (deliberately — expanding "St" ->
// "Street" corrupts the suburb "St Ives"). Tokens are re-cased in place, so it
// is safe + idempotent on already-clean input.

const AU_STATE_ABBREVS = new Set(['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT']);

// Non-state acronyms that must stay uppercase in a display address (BUG-3 fix
// 2026-07-02): without this, "PO BOX 245" title-cased to "Po Box 245".
const KEEP_UPPER = new Set(['PO', 'GPO', 'DPO']);

// Minor words that stay lowercase in the MIDDLE of a name (never the first
// token). Kept intentionally tiny; AU street/suburb names rarely use them.
const MINOR_WORDS = new Set(['of', 'the', 'and', 'at', 'on']);

/**
 * Title-case a single word token with AU-address-aware special cases.
 * Digit-bearing tokens (street numbers, "6/37-41", postcodes) pass through
 * untouched. Handles Mc-, O'-, and hyphenated names.
 */
function titleCaseToken(word: string): string {
    if (!word) return word;
    if (/\d/.test(word)) return word; // street number / unit range / postcode
    if (word.includes('-')) return word.split('-').map(titleCaseToken).join('-');
    const lower = word.toLowerCase();
    // "MCMAHONS" -> "McMahons" (Mc followed by >=2 letters)
    if (/^mc[a-z]{2,}$/.test(lower)) {
        return 'Mc' + lower.charAt(2).toUpperCase() + lower.slice(3);
    }
    // "O'CONNOR" -> "O'Connor", "D'ANGELO" -> "D'Angelo"
    const apos = word.match(/^([a-z])'([a-z].*)$/i);
    if (apos) {
        // apos[1]/apos[2] are mandatory capture groups (no optional/alternation),
        // so under a truthy match they are always present — the `?? ''` fallbacks
        // are provably unreachable and keep the re-cased output byte-identical,
        // satisfying noUncheckedIndexedAccess without a non-null assertion.
        const initial = apos[1] ?? '';
        const rest = apos[2] ?? '';
        return initial.toUpperCase() + "'" + titleCaseToken(rest);
    }
    return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Format an Australian address for human-readable display.
 * Title-cases street + suburb words, keeps the state abbreviation uppercase,
 * preserves unit/number tokens and postcode, and does not expand street-type
 * abbreviations. Case-only, structure-preserving, idempotent.
 *
 * "6/37-41 VICTORIA STREET, EPPING NSW 2121"
 *   -> "6/37-41 Victoria Street, Epping NSW 2121"
 *
 * @param {string} raw
 * @returns {string} the re-cased address, or '' for empty/non-string input
 */
export function formatAddressForDisplay(raw: any): string {
    if (!raw || typeof raw !== 'string') return '';
    const trimmed = raw.replace(/\s+/g, ' ').trim();
    if (!trimmed) return '';
    return trimmed.split(' ').map((token, idx) => {
        // Peel trailing punctuation (comma/period) so we case the word, then reattach.
        const m = token.match(/^(.*?)([,.]*)$/);
        const core = m ? m[1] : token;
        const trail = m ? m[2] : '';
        if (!core) return token;
        const upper = core.toUpperCase();
        if (AU_STATE_ABBREVS.has(upper)) return upper + trail; // NSW/VIC/... stay upper
        if (KEEP_UPPER.has(upper)) return upper + trail; // PO / GPO / DPO stay upper
        if (idx > 0 && !/\d/.test(core) && MINOR_WORDS.has(core.toLowerCase())) {
            return core.toLowerCase() + trail;
        }
        return titleCaseToken(core) + trail;
    }).join(' ');
}

/**
 * Build a dedup key for a property from strata plan and/or address.
 * Returns null if neither is available.
 */
export function buildPropertyDedupKey(strataplan: any, address: any) {
    if (strataplan) {
        return `sp:${normalizeStrataPlan(strataplan)}`;
    }
    if (address) {
        return `addr:${normalizeAddress(address)}`;
    }
    return null;
}
